import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import type { Appointment, Service, User, Location } from '../../lib/db/types';
import { getPublicAppointment } from '../../lib/bookingApi';

interface AppointmentWithId extends Appointment {
  id: string;
}

interface ServiceWithId extends Service {
  id: string;
}

interface UserWithId extends User {
  id: string;
}

interface LocationWithId extends Location {
  id: string;
}

interface AppointmentDetails {
  appointment: AppointmentWithId;
  service: ServiceWithId;
  provider: UserWithId;
  location?: LocationWithId | null;
  tenantName: string;
  tenantSlug: string;
}

type AppointmentStatus = 'confirmed' | 'pending' | 'completed' | 'canceled' | 'no_show';

export function AppointmentManagePage() {
  const { publicAccessHash } = useParams<{ publicAccessHash: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<AppointmentDetails | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  // Fetch appointment by public access hash
  useEffect(() => {
    if (!publicAccessHash) {
      setError('Invalid appointment link');
      setLoading(false);
      return;
    }

    const loadAppointment = async () => {
      try {
        const result = await getPublicAppointment(publicAccessHash);
        const appointmentData = {
          ...result.appointment,
          startTime: Timestamp.fromDate(new Date(result.appointment.startTime)),
          endTime: Timestamp.fromDate(new Date(result.appointment.endTime)),
        } as AppointmentWithId;

        // Check if appointment is in the past
        const now = Timestamp.now();
        if (appointmentData.startTime.toMillis() < now.toMillis()) {
          setError('This appointment has already passed.');
          setLoading(false);
          return;
        }

        const service = result.service as ServiceWithId | null;
        const provider = result.provider as unknown as UserWithId | null;
        const location = result.location as unknown as LocationWithId | null;

        if (!service) {
          setError('Service information not found');
          setLoading(false);
          return;
        }

        if (!provider) {
          setError('Provider information not found');
          setLoading(false);
          return;
        }

        setDetails({
          appointment: appointmentData,
          service,
          provider,
          location,
          tenantName: result.tenant?.name || 'Business',
          tenantSlug: result.tenant?.slug || '',
        });
      } catch (err: any) {
        console.error('Error loading appointment:', err);
        setError('Failed to load appointment information');
      } finally {
        setLoading(false);
      }
    };

    loadAppointment();
  }, [publicAccessHash]);

  const formatDateTime = (timestamp: Timestamp): string => {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hr`;
    return `${hours} hr ${remainingMinutes} min`;
  };

  const getProviderName = (provider: UserWithId): string => {
    return (provider as any).name || [provider.firstName, provider.lastName].filter(Boolean).join(' ') || provider.email || 'Provider';
  };

  const getStatusColor = (status: AppointmentStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'canceled':
        return 'error';
      case 'completed':
        return 'success';
      case 'no_show':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!details) return;

    setCancelling(true);
    try {
      const cancelAppointment = httpsCallable(functions, 'cancelAppointment');
      await cancelAppointment({ publicAccessHash });

      setCancelSuccess(true);
      setCancelDialogOpen(false);
      
      // Update local state
      setDetails({
        ...details,
        appointment: {
          ...details.appointment,
          status: 'canceled',
        },
      });
    } catch (err: any) {
      console.error('Error canceling appointment:', err);
      setError('Failed to cancel appointment. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelClose = () => {
    setCancelDialogOpen(false);
  };

  const handleRescheduleClick = () => {
    if (!details) return;

    navigate(`/book/${details.tenantSlug}/service/${details.service.id}/provider/${details.provider.id}`, {
      state: { reschedulePublicAccessHash: publicAccessHash },
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CloseIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please check your email for the correct link or contact the business directly.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // No details state (shouldn't happen, but handle it)
  if (!details) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Appointment not found
        </Alert>
      </Box>
    );
  }

  const { appointment, service, provider, location, tenantName } = details;
  const isCanceled = appointment.status === 'canceled';
  const isCompleted = appointment.status === 'completed';
  const canModify = !isCanceled && !isCompleted;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        pb: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h5"
            component="h1"
            fontWeight={600}
            sx={{ color: 'text.primary' }}
          >
            {tenantName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your appointment
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md">
        {/* Success message after cancellation */}
        {cancelSuccess && (
          <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
            Your appointment has been successfully canceled.
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Status Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon color="primary" />
                <Typography variant={isMobile ? 'h6' : 'h5'} component="h2" fontWeight={600}>
                  Appointment Details
                </Typography>
              </Box>
              <Chip
                label={appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                color={getStatusColor(appointment.status)}
                sx={{ fontWeight: 500 }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Service Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {service.name}
              </Typography>
              {service.description && (
                <Typography variant="body2" color="text.secondary">
                  {service.description}
                </Typography>
              )}
            </Box>

            {/* Details Grid */}
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                mb: 4,
              }}
            >
              {/* Date & Time */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <AccessTimeIcon color="action" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Date & Time
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(appointment.startTime)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Duration: {formatDuration(service.duration)}
                  </Typography>
                </Box>
              </Box>

              {/* Provider */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <PersonIcon color="action" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Provider
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {getProviderName(provider)}
                  </Typography>
                </Box>
              </Box>

              {/* Location */}
              {location && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <LocationIcon color="action" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {location.name}
                    </Typography>
                    {location.address && (
                      <Typography variant="body2" color="text.secondary">
                        {location.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Actions */}
            {canModify ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={<ScheduleIcon />}
                  onClick={handleRescheduleClick}
                  fullWidth={isMobile}
                  sx={{ minWidth: 150 }}
                >
                  Reschedule
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="large"
                  startIcon={<CloseIcon />}
                  onClick={handleCancelClick}
                  fullWidth={isMobile}
                  sx={{ minWidth: 150 }}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Alert
                severity={isCanceled ? 'error' : 'info'}
                sx={{ textAlign: 'center' }}
              >
                {isCanceled
                  ? 'This appointment has been canceled.'
                  : 'This appointment has been completed.'}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Help Text */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', mt: 3 }}
        >
          Need help? Contact {tenantName} directly.
        </Typography>
      </Container>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          Cancel Appointment?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel your appointment for{' '}
            <Box component="strong" sx={{ fontWeight: 700 }}>{service.name}</Box> on{' '}
            <Box component="strong" sx={{ fontWeight: 700 }}>{formatDateTime(appointment.startTime)}</Box>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} disabled={cancelling}>
            Keep Appointment
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            disabled={cancelling}
            autoFocus
          >
            {cancelling ? 'Canceling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AppointmentManagePage;
